import PropTypes from "prop-types";
import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import ProductApi from "@apis/ProductApi";

const SchemaContext = createContext();

export const SchemaProvider = ({ children }) => {

    const [childSchemaData, updateChildSchemaData] = useState(null);
    const [fullSchema, setFullSchema] = useState(null);

    const compiledAttrs = useRef({
        data: '',
        title: '',
        description: '',
        canonical: ''
    });


    const socials = useMemo(() => ({
        "sameAs": [
            "https://www.facebook.com/pages/LaCantina-Doors/167926889918291",
            "https://twitter.com/#!/lacantinadoors",
            "https://instagram.com/lacantinadoors/",
            "https://www.linkedin.com/company/lacantina-doors",
            "https://www.youtube.com/LaCantinaDoors1",
            "https://pinterest.com/lacantinadoors/"
        ]
    }), []);

    const lacSchema = useMemo(() => ({
        "@type": "LocalBusiness",
        "name": "LaCantina",
        "url": "https://www.lacantinadoors.com/",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "1875 Ord Way",
            "addressLocality": "Oceanside",
            "addressRegion": "CA",
            "postalCode": "92056",
            "addressCountry": "US"
        },
        "telephone": "888-221-1000",
        "logo": {
            "@type": "ImageObject",
            "url": "https://www.lacantinadoors.com/images/common-icons/LaCantina_Logo.svg"
        }
    }), []);

    const parseStringToObject = useCallback((el) => {
        if (el && typeof el === 'string') {
            try {
                // Attempt to parse the JSON string
                return JSON.parse(el);
            } catch (error) {
                // If parsing fails, attempt to repair by adding quotes around property names
                try {
                    const repairedString = el.replace(/'/g, '"').replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)(\s*:)/g, '$1"$2"$3');
                    return JSON.parse(repairedString);
                } catch (repairError) {
                    // If repair also fails, return null or handle the error accordingly
                    console.error("Failed to parse and repair JSON:", repairError);
                    return null;
                }
            }
        } else {
            return el;
        }
    }, []);

    const checkCurrentUrlWithRegex = (regex) => {
        const currentUrl = window.location.href;
        const pattern = new RegExp(regex);
        return pattern.test(currentUrl);
    }

    

    const urlSchemaPatterns = useCallback(async (data, title) => {

        // SEO regex patterns
        const regexPatterns = {
            home: /^(.*\.com\/|.*\.local\/)$/,
            blog: /^.*?\/blog$/,
            blogpost: /^.*?\/blog.*?$/,
            learnfeatured: /^.*?\/learn\/featured-projects$/,
            learnvideos: /^.*?\/learn\/interactive-videos$/,
            education: /^.*?\/learn.*?$/,
            professionals: /^.*?\/professionals\/.*?$/,

            faq: /^.*?\/faqs$/,
            organization: /^.*?\/about.*?$|^.*?\/careers$|^.*?\/become-dealer$|^.*?\/request-quote$|^.*?\/request-service$|^.*?\/sitemap$|^.*?\/terms-of-use$/,
            products: /^(.*?\.com\/products\/.*?|.*?\.local\/products\/.*?)$/,
            docs: /^.*?\/pdf\/.*?\.pdf.*?$|^.*?\/support\/installation$|^.*?\/pdfViewer.*?$/,
            support: /^.*?\/support.*?$/,

        };

        // Get the current URL and check it against each pattern using a switch statement
        for (const patternName in regexPatterns) {

            let isMatch = checkCurrentUrlWithRegex(regexPatterns[patternName]);

            switch (patternName) {

                // home CMS page
                case 'home':
                    if (isMatch) {
                        return {
                            "@type": "WebSite",
                            ...socials,
                            "publisher": {
                                ...lacSchema
                            }

                        }
                    }
                    break;

                // regex matching CMS pages
                case 'organization':
                    if (isMatch) {
                        lacSchema["@type"] = ["Organization", "WebPage"]
                        lacSchema["name"] = title
                        return lacSchema
                    }
                    break;

                // static, adds to data.SchemaOrg from BlogDetailPage.jsx (already has @type)
                case 'blogpost':
                    if (isMatch) return { "author": { ...lacSchema } }
                    break;

                // regex matching CMS blog pages, post list schema from context populated by ContentDirectoryBlade component
                case 'blog':
                case 'learnfeatured':
                    if (isMatch) {
                        return {
                            "@type": ["Blog", "WebPage"],
                            "blogPosts": childSchemaData,
                            "author": {
                                ...lacSchema
                            }
                        }
                    }
                    break;

                // regex matching learn/interactive-videos CMS page
                case 'learnvideos':
                    if (isMatch) return { 
                        "@type": ["VideoGallery", "WebPage", "HowTo"],
                        "step": [
                            {
                              "@type": "HowToStep",
                              "text": "Watch the videos."
                            },
                            {
                                "@type": "HowToStep",
                                "text": "Learn more about LaCantina products."
                            },
                        ],
                        ...interactiveVideoSchemaCompiler(data),  // replaces mainEntity, publisher set to lacSchema
                        "publisher": {
                            ...lacSchema
                        }
                     }
                    break;

                // regex matching remaining /learn CMS page
                case 'education':
                    if (isMatch) return { 
                        "@type": ["WebPage", "HowTo"] ,
                        "step": [
                            {
                              "@type": "HowToStep",
                              "text": "Read the articles."
                            },
                            {
                                "@type": "HowToStep",
                                "text": "Learn more about LaCantina products."
                              },
                        ],
                    }
                    break;

                // regex matching remaining /professionals CMS page
                case 'professionals':
                    if (isMatch) return { "@type": ["WebPage", "Audience"] }
                    break;

                // regex matching remaining /faq CMS page
                case 'faq':
                    if (isMatch) return { 
                        "@type": "FAQPage",
                        ...faqSchemaCompiler(data), // replaces mainEntity, publisher set to lacSchema
                        "publisher": {
                            ...lacSchema
                        }
                     }
                    break;

                case 'products':
                    if (isMatch) {
                        if (/^\/products\/[^/]+\/[^/]+(?:\/|$)/.test(window.location.pathname)) {
                            return {
                                "@type": ["WebPage", "Product"],
                                ...socials,
                                
                                ...(await productSchemaCompiler(data))
                            };
                        } else {
                            return { 
                                "@type": ["WebPage", "Product"],
                                ...socials,
                                "image": {
                                    "@type": "ImageObject",
                                    "url": "https://www.lacantinadoors.com/images/common-icons/LaCantina_Logo.svg"
                                },
                                "offers": {
                                    "@type": "AggregateOffer",
                                    "lowPrice": "10000",
                                    "highPrice": "50000",
                                    "priceCurrency": "USD",
                                    "seller": {
                                      ...lacSchema
                                    },
                                  },
                            };
                        }
                    }
                    break;

                // regex matching remaining /support CMS page
                case 'support':
                    if (isMatch) return { "@type": "WebPage" }
                    break;

                case 'docs':
                    if (isMatch) return { "@type": ["WepbPage", "HowTo"] }
                    break;

                default:
                    return {
                        "@type": "WebSite",
                        ...socials,
                        "publisher": {
                            ...lacSchema
                        }

                    }
            }

        }
    }, [lacSchema, socials, childSchemaData, productSchemaCompiler]);


    // faq is a blade on a cms page, so we are pulling the data from the multiple blades used to build the faq
    const faqSchemaCompiler = (data) => {

        const htmlToPlainText = (html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.body.textContent || "";
          }

        const faqElements = data.elements.filter(element => element.render_name === 'faq');

        // Create an array to store the FAQ schema
        const faqSchema = [];

        // Iterate through filtered FAQ elements
        faqElements.forEach(faqElement => {

            const qa_combo = faqElement?.value?.elements ? faqElement.value.elements.filter(subElement => subElement.render_name === 'qa_combo') : [];

            qa_combo.forEach(qa => {

                if(qa?.value?.elements){

                    // Extract questions and answers
                    const questionElement = qa.value.elements.find(subElement => subElement.render_name === 'question');
                    const question = questionElement ? htmlToPlainText(questionElement.value) : "";

                    const answerElement = qa.value.elements.find(subElement => subElement.render_name === 'answer');
                    const answer = answerElement ? htmlToPlainText(answerElement.value) : "";
                    

                    const faqItem = {
                        "@type": "Question",
                        "name": question,
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": answer || "" // Handle the case where the answer may not exist
                        }
                    }

                    // Add the FAQ item to the schema array
                    faqSchema.push(faqItem);

                }
                

            });
        });

        // Build FAQ schema
        const faqPage = {
            "mainEntity": faqSchema
        };

        return faqPage

    };

    const formatString = (input) => {

        // EX: "folding-doors/aluminum-thermally-controlled"
        // Step 1: Split the input string by '/'
        const parts = input.split('/');
    
        // Transform each part
        const transformedParts = parts.map(part => {
            // Step 2: Split by '-' and capitalize each word
            const words = part.split('-').map(word => {
                // Step 3: Capitalize the first letter of each word
                return word.charAt(0).toUpperCase() + word.slice(1);
            });
            // Join the words back with spaces
            return words.join(' ');
        });
    
        // Step 4: Reverse the order of parts and join with spaces
        // RESULT: "Aluminum Thermally Controlled Folding Doors"
        return transformedParts.reverse().join(' ');
    }

    const productSchemaCompiler = useCallback(async (data) => {

        if(!data.path) return;

        const slug = data.path.replace('/products/', '');
        const api = new ProductApi();
        
        try {

            const product = await api.getProductDataBySlug(slug);

            let pageMain = document.getElementsByClassName('main');
            let firstImage;
            if(pageMain[0]) {
                firstImage = pageMain[0].querySelector('img');
            } else  {
                firstImage = {src: 'https://www.lacantinadoors.com/images/common-icons/LaCantina_Logo.svg'};
            }

            const productImage = product.splash_url ? { "image": product.splash_url } : {"image": firstImage.src};
            const material = product.minor_label ? { "material": product.minor_label } : {};
            const category = product.major_label ? { "category": product.major_label } : {};

            

            const relatedProducts = product.related_products ? { "isRelatedTo": [
                ...product.related_products.map(key=>{
                    return {
                        "@type": "Product",
                        "name": formatString(key)
                    }
                }),
              ] } : {};

            const colors = product.sections.find((section)=>section.label === 'Colors');
            const colorOptions = [];
            if(colors){
                colors.content?.forEach((type)=>{
                    if(type.swatches){
                        type.swatches.forEach((swatch)=>{
                            colorOptions.push(swatch.swatch_label)
                        })
                    }
                })
            }
            const colorSchema = colorOptions.length ? { "color": colorOptions } : {};

            const productSchema = {
                "name":    data.metaTitle || `${product.minor_label} ${product.major_label}` || data.title,
                "description": data.metaDescription || data.metaTitle || `${product.minor_label} ${product.major_label}`,
                ...productImage,
                ...material, 
                ...category,
                ...relatedProducts,
                ...colorSchema,
                "offers": {
                    "@type": "AggregateOffer",
                    "priceCurrency": "USD",
                    "availability": "https://schema.org/InStock",
                    "lowPrice": "10000",
                    "highPrice": "50000",
                    "seller": {
                      ...lacSchema
                    },
                  },
                "brand": {
                    "@type": "Brand",
                    "name": "LaCantina"
                },
                "manufacturer": {
                    ...lacSchema
                },
               
            };

            return productSchema;

        } catch (error) {
            console.error('Error fetching product data:', error);
            throw error; 
        }
    }, [lacSchema]);
    
    

    const interactiveVideoSchemaCompiler = (data) => {

        // normally we would only look for the known blade, but since there are several blades that may support video
        // we will check each elements children for a video element

        const videoElements = [];

        if(!data.elements) return {}

        data.elements.forEach(element => {

            if(element?.value?.elements){

                element.value.elements.forEach(childElement => {

                    if(childElement.render_name === 'video'){

                        // Extract questions and answers
                        const providerElement = childElement.value.elements.find(subElement => subElement.render_name === 'video_provider');
                        const provider = providerElement.value;

                        const videoIdElement = childElement.value.elements.find(subElement => subElement.render_name === 'video_id');
                        const videoId = videoIdElement.value;

                        const updated_at = childElement.value.updated_at;

                        // we also only know the video URL's...

                        const embedMap = {
                            eko: `https://video.eko.com/v/${videoId}/embed`,
                            vimeo: `https://player.vimeo.com/video/${videoId}`,
                            youtube: `https://www.youtube.com/embed/${videoId}`,
                            vzaar: `https://view.vzaar.com/${videoId}/player`,
                            raw_url: videoId
                          };

                        videoElements.push({
                            "@type": `VideoObject`,
                            "name": 'LaCantina Interactive Video hosted at ' + provider.toUpperCase(),
                            "uploadDate": updated_at,
                            "thumbnailUrl": "https://www.lacantinadoors.com/images/common-icons/LaCantina_Logo.svg",
                            "embedUrl": embedMap[provider],
                            "contentUrl": embedMap[provider],
                          });
                    }

                })
            }
        })
        return {
            video: videoElements
        }
    }

    const compiledSchemaFunc = useCallback( async (data, title, description, canonical) => {

        compiledAttrs.current = {data, title, description, canonical}

        let newSchema = {
            "@context": "https://schema.org",
            "url": canonical,
            "name": title,
            "description": description,
            "mainEntity": lacSchema,
            ...(await urlSchemaPatterns(data, title)),
            ...parseStringToObject(data.schemaOrg),
        };

        setFullSchema(newSchema);

        // return newSchema
    }, [lacSchema, urlSchemaPatterns, parseStringToObject, setFullSchema]);

    const addBreadCrumbSchema = (breadcrumbData) => {

        let bcSchema = {
            ...fullSchema,
            ...breadcrumbData
        }

        setFullSchema(bcSchema)
    }

    useEffect(() => {
        compiledSchemaFunc(compiledAttrs.current.data, compiledAttrs.current.title, compiledAttrs.current.description, compiledAttrs.current.canonical);
    }, [childSchemaData, compiledSchemaFunc]);

    return (
        <SchemaContext.Provider value={{ updateChildSchemaData, compiledSchemaFunc, fullSchema, addBreadCrumbSchema }}>
            {children}
        </SchemaContext.Provider>
    );
};

export const useSchema = () => {
    const context = useContext(SchemaContext);
    if (!context) {
        throw new Error('useSchema must be used within a SchemaProvider');
    }
    return context;
};

SchemaProvider.propTypes = {
    children: PropTypes.node,
};